import { type ContactCandidate } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { type IntakeDraft } from '#app/routes/mocra+/_form+/intake/intake-draft.ts'

export const CONTACT_DIRECTORY_SECTION = 'contactDirectory' as const

type MaybeString = string | null | undefined

export type ContactDirectoryEntry = {
	id: string
	name: string
	email: string
	title?: string
	phone?: string
	source?: string
	updatedAt: number
}

export type ContactInput =
	| ContactDirectoryEntry
	| ContactCandidate
	| ContactDraftSeed
	| {
			id?: MaybeString
			name?: MaybeString
			email?: MaybeString
			title?: MaybeString
			phone?: MaybeString
			source?: MaybeString
	  }

export type UpsertOptions = {
	readonly source?: string
}

export function normalizeDirectoryEntries(
	raw: unknown,
): ContactDirectoryEntry[] {
	if (!Array.isArray(raw)) return []
	return sortEntries(dedupeEntries(sanitizeEntries(raw as ContactInput[])))
}

export function directoriesEqual(
	current: readonly ContactDirectoryEntry[] | null | undefined,
	next: readonly ContactDirectoryEntry[],
): boolean {
	if (!current) return false
	if (current.length !== next.length) return false
	for (let index = 0; index < current.length; index += 1) {
		const left = current[index]
		const right = next[index]
		if (!left || !right) return false
		if (!directoryEntriesEqual(left, right)) return false
	}
	return true
}

export function upsertDirectoryEntry(
	previous: readonly ContactDirectoryEntry[],
	input: ContactInput,
	options: UpsertOptions = {},
): {
	entries: ContactDirectoryEntry[]
	entry: ContactDirectoryEntry | null
} {
	const hasInputId =
		typeof (input as { id?: unknown }).id === 'string' &&
		(input as { id: string }).id.trim().length > 0

	const normalized = normalizeContactInput(input)
	if (!normalized) {
		return { entries: [...previous], entry: null }
	}

	let candidate: ContactDirectoryEntry = {
		...normalized,
		source: normalized.source ?? options.source ?? normalized.source,
	}

	if (!hasInputId) {
		const emailMatch = previous.find(
			(candidateEntry) =>
				candidateEntry.email.toLowerCase() === normalized.email.toLowerCase(),
		)
		if (emailMatch) {
			candidate = { ...candidate, id: emailMatch.id }
		}
	}

	const existingIndex = findMatchingIndex(previous, candidate)
	if (existingIndex === -1) {
		return {
			entries: sortEntries([...previous, candidate]),
			entry: candidate,
		}
	}

	const existing = previous[existingIndex]
	if (!existing) {
		return {
			entries: sortEntries([...previous, candidate]),
			entry: candidate,
		}
	}

	const merged: ContactDirectoryEntry = {
		id: existing.id,
		name: candidate.name || existing.name,
		email: candidate.email || existing.email,
		title: candidate.title ?? existing.title,
		phone: candidate.phone ?? existing.phone,
		source:
			candidate.source ?? existing.source ?? options.source ?? existing.source,
		updatedAt: Math.max(existing.updatedAt, candidate.updatedAt),
	}

	if (directoryEntriesEqual(existing, merged)) {
		return {
			entries: [...previous],
			entry: existing,
		}
	}

	const next = [...previous]
	next[existingIndex] = merged
	return {
		entries: sortEntries(next),
		entry: merged,
	}
}

export function removeDirectoryEntry(
	previous: readonly ContactDirectoryEntry[],
	id: string,
): ContactDirectoryEntry[] {
	return previous.filter((entry) => entry.id !== id)
}

export function seedContactsFromSections(
	sections: IntakeDraft,
): ContactDirectoryEntry[] {
	const seeds: ContactDirectoryEntry[] = []

	const primaryContacts = sections['primaryContacts'] as
		| PrimaryContactsSeed
		| undefined
	if (primaryContacts?.contacts?.length) {
		for (const contact of primaryContacts.contacts) {
			const entry = normalizeContactInput(contact)
			if (entry) {
				entry.source ??= 'Primary contacts'
				seeds.push(entry)
			}
		}
	}

	const recordsAccess = sections['recordsAccess'] as
		| RecordsAccessSeed
		| undefined
	if (recordsAccess?.custodians?.length) {
		for (const custodian of recordsAccess.custodians) {
			const entry = normalizeContactInput({
				id: custodian.contactId,
				name: custodian.fallbackName,
				email: custodian.fallbackEmail,
				phone: custodian.fallbackPhone,
				title: custodian.fallbackTitle ?? 'Records custodian',
			})
			if (entry) {
				entry.source ??= 'Records access'
				seeds.push(entry)
			}
		}
	}

	const adverseEvents = sections['adverseEvents'] as
		| AdverseEventsSeed
		| undefined
	if (adverseEvents) {
		const collections: Array<AdverseEventContactSeed[] | undefined> = [
			adverseEvents.seriousEventContacts,
			adverseEvents.followUpContacts,
			adverseEvents.trendAlertContacts,
		]
		const legacyEscalation = adverseEvents.escalation
		if (legacyEscalation) {
			collections.push(
				legacyEscalation.seriousEventContacts,
				legacyEscalation.potentialRecallContacts,
				legacyEscalation.complaintFollowUpContacts,
				legacyEscalation.trendAlertContacts,
			)
		}
		for (const collection of collections) {
			if (!collection) continue
			for (const contact of collection) {
				const entry = normalizeContactInput({
					name: contact.name,
					email: contact.email,
					phone: contact.phone,
					title: contact.role,
				})
				if (entry) {
					entry.source ??= 'Adverse event escalation'
					seeds.push(entry)
				}
			}
		}
	}

	return sortEntries(dedupeEntries(seeds))
}

export function mapEntriesToCandidates(
	entries: readonly ContactDirectoryEntry[],
): ContactCandidate[] {
	return entries.map(entryToCandidate)
}

export function normalizeContactInput(
	input: ContactInput,
): ContactDirectoryEntry | null {
	return normalizeCandidate(input)
}

export function entryToCandidate(
	entry: ContactDirectoryEntry,
): ContactCandidate {
	return {
		id: entry.id,
		name: entry.name,
		email: entry.email,
		phone: entry.phone,
		source: entry.title ?? entry.source,
	}
}

function normalizeCandidate(input: ContactInput): ContactDirectoryEntry | null {
	const idValue = toOptionalString((input as ContactDirectoryEntry).id)
	const nameValue =
		toOptionalString((input as ContactDirectoryEntry).name) ??
		toOptionalString((input as ContactDraftSeed).name)
	const emailValue =
		toOptionalString((input as ContactDirectoryEntry).email) ??
		toOptionalString((input as ContactDraftSeed).email)
	const titleValue =
		toOptionalString((input as ContactDirectoryEntry).title) ??
		toOptionalString((input as ContactDraftSeed).title)
	const phoneValue =
		toOptionalString((input as ContactDirectoryEntry).phone) ??
		toOptionalString((input as ContactDraftSeed).phone)
	const sourceValue =
		toOptionalString((input as ContactDirectoryEntry).source) ??
		toOptionalString((input as ContactCandidate).source)
	const updatedAtValue =
		typeof (input as ContactDirectoryEntry).updatedAt === 'number'
			? (input as ContactDirectoryEntry).updatedAt
			: undefined

	const email = (emailValue ?? '').trim()
	if (!email) return null

	const name = (nameValue ?? '').trim()

	return {
		id: idValue?.trim() || generateId(),
		name: name || email,
		email,
		title: titleValue?.trim() || undefined,
		phone: phoneValue?.trim() || undefined,
		source: sourceValue?.trim() || undefined,
		updatedAt:
			updatedAtValue !== undefined && Number.isFinite(updatedAtValue)
				? updatedAtValue
				: Date.now(),
	}
}

function findMatchingIndex(
	entries: readonly ContactDirectoryEntry[],
	entry: ContactDirectoryEntry,
): number {
	if (entry.id) {
		return entries.findIndex((candidate) => candidate.id === entry.id)
	}
	return -1
}

function directoryEntriesEqual(
	left: ContactDirectoryEntry,
	right: ContactDirectoryEntry,
): boolean {
	return (
		left.id === right.id &&
		left.name === right.name &&
		left.email === right.email &&
		left.title === right.title &&
		left.phone === right.phone &&
		left.source === right.source &&
		left.updatedAt === right.updatedAt
	)
}

function sanitizeEntries(
	entries: readonly ContactInput[],
): ContactDirectoryEntry[] {
	return entries
		.map((entry) => normalizeContactInput(entry))
		.filter((entry): entry is ContactDirectoryEntry => entry !== null)
}

function dedupeEntries(
	entries: readonly ContactDirectoryEntry[],
): ContactDirectoryEntry[] {
	const byId = new Map<string, ContactDirectoryEntry>()

	for (const entry of entries) {
		const key = entry.id
		if (!key) continue
		const existing = byId.get(key)
		if (!existing) {
			byId.set(key, entry)
			continue
		}
		const merged = mergeEntries(existing, entry)
		byId.set(merged.id, merged)
	}

	return Array.from(byId.values())
}

function mergeEntries(
	target: ContactDirectoryEntry,
	source: ContactDirectoryEntry,
): ContactDirectoryEntry {
	return {
		id: target.id,
		name: source.name || target.name,
		email: source.email || target.email,
		title: source.title ?? target.title,
		phone: source.phone ?? target.phone,
		source: source.source ?? target.source,
		updatedAt: Math.max(
			target.updatedAt ?? Date.now(),
			source.updatedAt ?? Date.now(),
		),
	}
}

function sortEntries(
	entries: readonly ContactDirectoryEntry[],
): ContactDirectoryEntry[] {
	return [...entries].sort((a, b) => {
		const nameA = a.name.toLowerCase()
		const nameB = b.name.toLowerCase()
		if (nameA < nameB) return -1
		if (nameA > nameB) return 1
		return a.email.toLowerCase().localeCompare(b.email.toLowerCase())
	})
}

function toOptionalString(value: MaybeString): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

function generateId(): string {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID()
	}
	return `contact-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

type ContactDraftSeed = {
	id?: MaybeString
	name?: MaybeString
	email?: MaybeString
	title?: MaybeString
	phone?: MaybeString
	source?: MaybeString
}

type PrimaryContactsSeed = {
	contacts?: Array<{
		id?: MaybeString
		title?: MaybeString
		name?: MaybeString
		email?: MaybeString
		phone?: MaybeString
	}>
}

type RecordsAccessSeed = {
	custodians?: Array<{
		contactId?: MaybeString
		fallbackName?: MaybeString
		fallbackEmail?: MaybeString
		fallbackTitle?: MaybeString
		fallbackPhone?: MaybeString
	}>
}

type AdverseEventsSeed = {
	seriousEventContacts?: AdverseEventContactSeed[]
	followUpContacts?: AdverseEventContactSeed[]
	trendAlertContacts?: AdverseEventContactSeed[]
	escalation?: {
		seriousEventContacts?: AdverseEventContactSeed[]
		potentialRecallContacts?: AdverseEventContactSeed[]
		complaintFollowUpContacts?: AdverseEventContactSeed[]
		trendAlertContacts?: AdverseEventContactSeed[]
	}
}

type AdverseEventContactSeed = {
	role?: MaybeString
	name?: MaybeString
	email?: MaybeString
	phone?: MaybeString
}
